import React from 'react';

import { QBox, QStack, QText } from '@captivateiq/quotient';

import type { TableBlock } from '@captivateiq/assistant/types';

type Props = { block: TableBlock };

const tableStyle: React.CSSProperties = { borderCollapse: 'collapse', fontSize: '12px', width: '100%' };
const headerRowStyle: React.CSSProperties = { backgroundColor: '#FAFBFC' };
const headerCellStyle: React.CSSProperties = {
  borderBottom: '1px solid #E5E7EB',
  color: '#6B7280',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.025em',
  padding: '8px 12px',
  textAlign: 'left',
  textTransform: 'uppercase',
};
const cellStyle: React.CSSProperties = { color: '#374151', padding: '8px 12px' };

// Agent-generated content (cells, headers, captions) is rendered as JSX text. React's built-in
// escaping handles XSS. Never switch to dangerouslySetInnerHTML for these fields.
export const TableBlockRenderer = ({ block }: Props) => {
  return (
    <QBox
      sx={{
        backgroundColor: '#fff',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '8px',
        mb: 2,
        overflow: 'hidden',
      }}
    >
      <QStack direction="column" spacing="0">
        {block.title && (
          <QText
            sx={{
              borderBottom: '1px solid',
              borderColor: 'grey.100',
              color: 'grey.700',
              fontSize: '13px',
              fontWeight: 600,
              p: 2,
            }}
          >
            {block.title}
          </QText>
        )}
        <QBox sx={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={headerRowStyle}>
                {block.headers.map((header, headerIdx) => (
                  // eslint-disable-next-line react/no-array-index-key -- table headers are a static array, never reordered
                  <th key={`header-${headerIdx}`} style={headerCellStyle}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIdx) => (
                <tr
                  // eslint-disable-next-line react/no-array-index-key -- table rows are a static array, never reordered
                  key={`row-${rowIdx}`}
                  style={{ borderBottom: rowIdx === block.rows.length - 1 ? 'none' : '1px solid #F3F4F6' }}
                >
                  {row.map((cell, cellIdx) => (
                    // eslint-disable-next-line react/no-array-index-key -- row cells are a static array, never reordered
                    <td key={`cell-${cellIdx}`} style={cellStyle}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </QBox>
        {block.caption && (
          <QText
            sx={{ backgroundColor: 'grey.50', color: 'grey.500', fontSize: '11px', fontStyle: 'italic', px: 2, py: 1 }}
          >
            {block.caption}
          </QText>
        )}
      </QStack>
    </QBox>
  );
};
